import { useCallback, useEffect, useState } from 'react'
import {
	Alert,
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Pagination,
	Select,
	Stack,
	TextField,
	Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
	AVITO_AUTOLOAD_PROPERTY_RIGHTS,
	getAvitoAutoloadErrorMessage,
	includeInAvitoAutoload,
	listAvitoAutoloadObjects,
	previewAvitoAutoloadObject,
} from '../../utils/avitoAutoload'
import { presentAvitoDiagnostics } from '../../utils/avitoDiagnostics'

const PAGE_SIZE = 10

function AvitoAutoloadAddDialog({ open, account, onClose, onIncluded }) {
	const [search, setSearch] = useState('')
	const [page, setPage] = useState(1)
	const [result, setResult] = useState({ objects: [], total: 0 })
	const [selected, setSelected] = useState(null)
	const [propertyRights, setPropertyRights] = useState('Посредник')
	const [preview, setPreview] = useState(null)
	const [loading, setLoading] = useState(false)
	const [previewing, setPreviewing] = useState(false)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState('')

	const loadObjects = useCallback(async () => {
		if (!open || !account?.id) return
		setLoading(true)
		setError('')
		try {
			setResult(
				await listAvitoAutoloadObjects({
					classifiedAccountId: account.id,
					search,
					page,
					pageSize: PAGE_SIZE,
				})
			)
		} catch (requestError) {
			setError(getAvitoAutoloadErrorMessage(requestError))
		} finally {
			setLoading(false)
		}
	}, [account?.id, open, page, search])

	useEffect(() => {
		loadObjects()
	}, [loadObjects])

	useEffect(() => {
		if (!open) {
			setSearch('')
			setPage(1)
			setSelected(null)
			setPreview(null)
			setError('')
		}
	}, [open])

	const runPreview = useCallback(async (objectId) => {
		if (!objectId || !account?.id) return
		setPreviewing(true)
		setError('')
		try {
			setPreview(
				await previewAvitoAutoloadObject({
					objectId,
					classifiedAccountId: account.id,
					propertyRights,
				})
			)
		} catch (requestError) {
			setError(getAvitoAutoloadErrorMessage(requestError))
		} finally {
			setPreviewing(false)
		}
	}, [account?.id, propertyRights])

	useEffect(() => {
		setPreview(null)
		if (selected?.id) runPreview(selected.id)
	}, [runPreview, selected?.id])

	const handlePreview = () => runPreview(selected?.id)

	const handleInclude = async () => {
		if (!selected || !preview?.readyForAutoload) return
		setSaving(true)
		setError('')
		try {
			const response = await includeInAvitoAutoload({
				objectId: selected.id,
				classifiedAccountId: account.id,
				propertyRights,
			})
			onIncluded(response)
		} catch (requestError) {
			setError(getAvitoAutoloadErrorMessage(requestError))
			if (requestError?.response?.data?.error?.details) {
				setPreview(requestError.response.data.error.details)
			}
		} finally {
			setSaving(false)
		}
	}

	const handleOpenObjectEditor = () => {
		if (!selected?.id) return
		window.open(
			`/objects?editObjectId=${encodeURIComponent(selected.id)}`,
			'_blank',
			'noopener,noreferrer'
		)
	}

	const pages = Math.max(1, Math.ceil((result.total || 0) / PAGE_SIZE))
	const presentedDiagnostics = presentAvitoDiagnostics(preview?.diagnostics, {
		mode: 'candidate',
	})

	return (
		<Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
			<DialogTitle>Добавить объявление в автозагрузку</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					<Alert severity="info">
						Reldo проверит объявление и автоматически обновит данные для
						аккаунта «{account?.name}».
					</Alert>
					<TextField
						label="Поиск по названию или адресу"
						value={search}
						onChange={(event) => {
							setSearch(event.target.value)
							setPage(1)
						}}
						fullWidth
					/>
					{loading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<CircularProgress />
						</Box>
					) : result.objects.length === 0 ? (
						<Alert severity="info">Подходящие объекты не найдены.</Alert>
					) : (
						<Stack spacing={1}>
							{result.objects.map((object) => (
								<Button
									key={object.id}
									variant={selected?.id === object.id ? 'contained' : 'outlined'}
									disabled={!object.eligible}
									onClick={() => setSelected(object)}
									sx={{
										justifyContent: 'space-between',
										textAlign: 'left',
										py: 1.25,
										gap: 1,
									}}>
									<Box>
										<Typography component="span">{object.label}</Typography>
										{object.address && object.address !== object.label && (
											<Typography component="div" variant="caption">
												{object.address}
											</Typography>
										)}
									</Box>
									<Chip
										size="small"
										label={
											object.included
												? 'Уже в автозагрузке'
												: object.supported
													? 'Поддерживается'
													: 'Категория не поддерживается'
										}
									/>
								</Button>
							))}
						</Stack>
					)}
					{pages > 1 && (
						<Pagination
							count={pages}
							page={page}
							onChange={(_, value) => setPage(value)}
						/>
					)}
					<FormControl fullWidth disabled={!selected}>
						<InputLabel id="avito-property-rights-label">Право размещения</InputLabel>
						<Select
							labelId="avito-property-rights-label"
							label="Право размещения"
							value={propertyRights}
							onChange={(event) => setPropertyRights(event.target.value)}>
							{AVITO_AUTOLOAD_PROPERTY_RIGHTS.map((value) => (
								<MenuItem key={value} value={value}>
									{value}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{previewing && (
						<Stack direction="row" spacing={1} alignItems="center">
							<CircularProgress size={20} />
							<Typography color="text.secondary">
								Проверяем объявление…
							</Typography>
						</Stack>
					)}
					{preview && (
						<Alert severity={preview.readyForAutoload ? 'success' : 'warning'}>
							{preview.readyForAutoload
								? 'Объявление готово к добавлению.'
								: 'Объявление пока нельзя добавить в автозагрузку'}
							{presentedDiagnostics.missing.length > 0 && (
								<Box sx={{ mt: 1 }}>
									<Typography fontWeight={600}>
										Заполните обязательные данные объекта:
									</Typography>
									<Box component="ul" sx={{ mb: 0, mt: 0.5 }}>
										{presentedDiagnostics.missing.map((item) => (
											<li key={item.key}>{item.label || item.message}</li>
										))}
									</Box>
								</Box>
							)}
							{presentedDiagnostics.clarify.length > 0 && (
								<Box sx={{ mt: 1 }}>
									<Typography fontWeight={600}>Уточните данные:</Typography>
									<Box component="ul" sx={{ mb: 0, mt: 0.5 }}>
										{presentedDiagnostics.clarify.map((item) => (
											<li key={item.key}>{item.message}</li>
										))}
									</Box>
								</Box>
							)}
							{presentedDiagnostics.technical.length > 0 && (
								<Typography sx={{ mt: 1 }}>
									Не удалось подготовить объявление из-за внутренней ошибки.
								</Typography>
							)}
							{!preview.readyForAutoload && (
								<Stack
									direction={{ xs: 'column', sm: 'row' }}
									spacing={1}
									sx={{ mt: 2 }}>
									<Button variant="outlined" onClick={handleOpenObjectEditor}>
										Открыть объект для редактирования
									</Button>
									<Button onClick={handlePreview} disabled={previewing}>
										{previewing ? 'Проверка…' : 'Проверить снова'}
									</Button>
								</Stack>
							)}
							{presentedDiagnostics.raw.length > 0 && (
								<Accordion
									disableGutters
									elevation={0}
									sx={{ mt: 1, bgcolor: 'transparent' }}>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Typography variant="body2">
											Техническая информация
										</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Stack spacing={1}>
											{presentedDiagnostics.raw.map((item, index) => (
												<Box key={`${item.code}-${index}`}>
													<Typography variant="caption" component="div">
														{item.code}
														{item.canonicalFieldKey
															? ` · ${item.canonicalFieldKey}`
															: ''}
														{item.targetFieldKey
															? ` → ${item.targetFieldKey}`
															: ''}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{item.message}
													</Typography>
												</Box>
											))}
										</Stack>
									</AccordionDetails>
								</Accordion>
							)}
						</Alert>
					)}
					{error && <Alert severity="error">{error}</Alert>}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={saving}>
					Отмена
				</Button>
				<Button
					variant="contained"
					onClick={handleInclude}
					disabled={!preview?.readyForAutoload || saving}>
					{saving ? 'Добавление…' : 'Добавить объявление'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default AvitoAutoloadAddDialog
